import _ from 'lodash';
import Vue from 'vue';
import VueRouter from 'vue-router';
import NProgress from 'nprogress';

NProgress.configure({ showSpinner: false });
import store from '../store/store';

Vue.use(VueRouter);

const routes = [
  {
    path: '/settings/integrations/opportunity-chats',
    name: 'opportunity-chats',
    component: () => import('@/views/OpportunityChats.vue'),
    meta: { authRequired: true, title: 'Opportunity Chats' },
    beforeEnter(to, from, next) {
      next(store.getters['auth/isCurrentUserAdmin'] ? undefined : { name: 'home' });
    }
  },
  {
    path: '/settings/integrations/opportunity-chats/:id',
    name: 'opportunity-chat',
    component: () => import('@/views/OpportunityChat.vue'),
    meta: { authRequired: true, title: 'Opportunity Chat' },
    beforeEnter(to, from, next) {
      next(store.getters['auth/isCurrentUserAdmin'] ? undefined : { name: 'home' });
    }
  },
  {
    path: '/integrations/authorize',
    name: 'integration-authorize',
    component: () => import('@/views/IntegrationAuthorize.vue'),
    meta: { authRequired: true, allowWithoutOrg: true, title: 'Connect an AI client' }
  },
  {
    path: '/settings/integrations/ai-connections',
    alias: '/integrations/connections',
    name: 'integration-connections',
    component: () => import('@/views/IntegrationConnections.vue'),
    meta: { authRequired: true, title: 'AI Connectors' }
  },
  {
    path: '/settings/integrations/api-keys',
    name: 'integration-api-keys',
    // Keep key management hidden until organization-level rollout is enabled.
    redirect: { name: 'settings-integrations' }
  },
  {
    path: '/',
    name: 'home',
    redirect: '/dashboard',
    meta: {
      authRequired: true,
      title: 'Dashboard'
    }
  },
  {
    path: '/sign-up',
    name: 'sign-up',
    component: () => import('@/views/SignUp.vue'),
    meta: {
      title: 'Sign up'
    },
    async beforeEnter(routeTo, routeFrom, next) {
      const currentUser = await store.dispatch('auth/validate');
      if (currentUser && !_.isEmpty(currentUser.org)) {
        return next(_.get(routeTo, 'query.next') || { name: 'home' });
      } else {
        return next();
      }
    }
  },
  {
    path: '/sign-in',
    name: 'sign-in',
    component: () => {
      return import('@/views/SignIn.vue');
    },
    meta: {
      title: 'Sign in'
    },
    async beforeEnter(routeTo, routeFrom, next) {
      const currentUser = await store.dispatch('auth/validate');
      if (currentUser) {
        return next(_.get(routeTo, 'query.next') || { name: 'home' });
      } else {
        return next();
      }
    }
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('@/views/VerifyEmail.vue'),
    meta: {
      title: 'Verify Email'
    },
    beforeEnter(routeTo, routeFrom, next) {
      const currentUser = store.getters['auth/currentUser'];
      if (_.get(currentUser, 'emailVerified')) {
        next(_.get(routeTo, 'query.next') || { name: 'home' });
      } else {
        next();
      }
    }
  },
  {
    path: '/restore',
    name: 'restore',
    component: () => import('@/views/Restore.vue'),
    meta: {
      title: 'Restore'
    }
  },
  {
    path: '/new-password',
    name: 'newPassword',
    component: () => import('@/views/NewPassword.vue'),
    meta: {
      title: 'New password'
    },
    beforeEnter(routeTo, routeFrom, next) {
      if (!routeTo.query.token) {
        return next({ name: 'sign-in' });
      }
      next();
    }
  },
  {
    path: '/logout',
    name: 'logout',
    beforeEnter(routeTo, routeFrom, next) {
      store.dispatch('auth/logout').then(() => next({ name: 'sign-in' })); //! TODO
    }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => {
      if (store.getters['auth/isCurrentUserFundraiser'] && !store.getters['auth/isCurrentUserSuperuser']) {
        return import('@/views/DashboardFundraiser.vue');
      }
      return import('@/views/Dashboard.vue');
    },
    meta: {
      authRequired: true,
      title: 'Dashboard'
    }
  },
  {
    path: '/campaigns',
    name: 'campaigns',
    component: () => import('@/views/Campaigns/Index.vue'),
    meta: {
      authRequired: true,
      title: 'Campaigns'
    }
  },
  {
    path: '/campaigns/:id/fundraisers/new',
    name: 'campaign-fundraiser-new',
    component: () => import('@/views/Campaigns/FundraiserNew.vue'),
    meta: {
      authRequired: true,
      title: 'New Fundraiser'
    }
  },
  {
    path: '/campaigns/:id/fundraisers/:fundraiserId',
    name: 'campaign-fundraiser',
    component: () => import('@/views/Campaigns/Fundraiser.vue'),
    meta: {
      authRequired: true,
      title: 'Fundraiser'
    }
  },
  {
    path: '/campaigns/:id',
    component: () => import('@/views/Campaigns/Show.vue'),
    children: [
      { path: '', name: 'campaign', component: () => import('@/views/Campaigns/Checkout.vue') },
      { path: 'page', name: 'campaign-page', component: () => import('@/views/Campaigns/Page.vue') },
      { path: 'fundraisers', name: 'campaign-fundraisers', component: () => import('@/views/Campaigns/Fundraisers.vue') },
      { path: 'emails', name: 'campaign-emails', component: () => import('@/views/Campaigns/Emails.vue') },
      { path: 'installation', name: 'campaign-installation', component: () => import('@/views/Campaigns/Installation.vue') }
    ],
    meta: {
      authRequired: true,
      title: 'Campaign'
    }
  },
  {
    path: '/charges',
    name: 'charges',
    component: () => import('@/views/Charges/Index.vue'),
    meta: {
      authRequired: true,
      title: 'All transactions'
    }
  },
  {
    path: '/charges/offline/new',
    name: 'offline-donation-new',
    redirect: '/charges'
  },
  {
    path: '/charges/:id',
    name: 'charge',
    component: () => import('@/views/Charges/Show.vue'),
    meta: {
      authRequired: true,
      title: 'Transaction'
    }
  },
  {
    path: '/donations',
    name: 'donations',
    component: () => import('@/views/Recurring/Index.vue'),
    meta: {
      authRequired: true,
      title: 'Recurring'
    }
  },
  {
    path: '/donations/:id',
    name: 'donation',
    component: () => import('@/views/Recurring/Show.vue'),
    meta: {
      authRequired: true,
      title: 'Recurring'
    }
  },

  {
    path: '/donors',
    name: 'donors',
    component: () => import('@/views/Donors.vue'),
    meta: {
      authRequired: true,
      title: 'Supporters'
    }
  },
  {
    path: '/donors/new',
    name: 'donor-new',
    component: () => import('@/views/Donors/New.vue'),
    meta: {
      authRequired: true,
      title: 'New Supporter'
    },
    beforeEnter(routeTo, routeFrom, next) {
      const isSuperuser = store.getters['auth/isCurrentUserSuperuser'];
      if (!isSuperuser) {
        next('home');
      } else {
        next();
      }
    }
  },
  {
    path: '/donors/:id',
    name: 'donor',
    component: () => import('@/views/Donor.vue'),
    meta: {
      authRequired: true,
      title: 'Supporter'
    }
  },
  {
    path: '/items',
    name: 'items',
    component: () => import('@/views/Items/Index.vue'),
    meta: {
      authRequired: true,
      title: 'Items'
    },
    beforeEnter(routeTo, routeFrom, next) {
      const isAdmin = store.getters['auth/isCurrentUserAdmin'];
      if (!isAdmin) {
        next('home');
      } else {
        next();
      }
    }
  },
  {
    path: '/items/new',
    name: 'item-new',
    component: () => import('@/views/Items/Show.vue'),
    meta: {
      authRequired: true,
      title: 'New Item'
    },
    beforeEnter(routeTo, routeFrom, next) {
      const isAdmin = store.getters['auth/isCurrentUserAdmin'];
      if (!isAdmin) {
        next('home');
      } else {
        next();
      }
    }
  },
  {
    path: '/items/:id',
    name: 'item',
    component: () => import('@/views/Items/Show.vue'),
    meta: {
      authRequired: true,
      title: 'Item'
    },
    beforeEnter(routeTo, routeFrom, next) {
      const isAdmin = store.getters['auth/isCurrentUserAdmin'];
      if (!isAdmin) {
        next('home');
      } else {
        next();
      }
    }
  },
  {
    path: '/tools',
    name: 'tools',
    component: () => import('@/views/Tools/Index.vue'),
    meta: {
      authRequired: true,
      title: 'Tools'
    },
    beforeEnter(routeTo, routeFrom, next) {
      const isAdmin = store.getters['auth/isCurrentUserAdmin'];
      if (!isAdmin) {
        next('home');
      } else {
        next();
      }
    }
  },
  {
    path: '/settings',
    // name: 'settings',
    component: () => import('@/views/Settings/Index.vue'),
    children: [
      { path: '', name: 'settings-integrations', component: () => import('@/views/Settings/SettingsIntegrations.vue') },
      { path: 'portal', name: 'settings-portal', component: () => import('@/views/Settings/SettingsPortal.vue') },
      { path: 'entity/:id', name: 'settings-entities', component: () => import('@/views/Settings/SettingsEntities.vue') },
      { path: 'solicitors', name: 'settings-solicitors', component: () => import('@/views/Settings/SettingsSolicitors.vue') },
      { path: 'webhooks', name: 'settings-webhooks', component: () => import('@/views/Settings/SettingsWebhooks.vue') },
      { path: 'installation', name: 'settings-installation', component: () => import('@/views/Settings/SettingsInstallation.vue') }
    ],
    meta: {
      authRequired: true,
      title: 'Settings'
    },

    beforeEnter(routeTo, routeFrom, next) {
      const isAdmin = store.getters['auth/isCurrentUserAdmin'];
      if (!isAdmin) {
        next('home');
      } else {
        next();
      }
    }
  },
  {
    path: '/settings/integrations/hubspot',
    name: 'settings-integrations-hubspot',
    component: () => import('@/views/Settings/integrations/hubspot/Hubspot.vue'),
    meta: {
      authRequired: true,
      title: 'HubSpot'
    },
    beforeEnter(routeTo, routeFrom, next) {
      const isAdmin = store.getters['auth/isCurrentUserAdmin'];
      if (!isAdmin) {
        next('home');
      } else {
        next();
      }
    }
  },
  {
    path: '/admin',
    component: () => import('@/views/Admin.vue'),
    children: [
      { path: '', name: 'admin-organizations', component: () => import('@/views/Admin/Organizations.vue') },
      { path: 'donation-drafts', name: 'admin-donation-drafts', component: () => import('@/views/Admin/DonationDrafts.vue') }
    ],
    meta: {
      authRequired: true,
      title: 'Admin'
    },
    beforeEnter(routeTo, routeFrom, next) {
      const isSuperuser = store.getters['auth/isCurrentUserSuperuser'];
      if (!isSuperuser) {
        next('home');
      } else {
        next();
      }
    }
  },
  {
    path: '/admin/org/:id',
    name: 'admin-org',
    component: () => import('@/views/Orgs/Org.vue'),
    children: [
      { path: '', name: 'org-donations', component: () => import('@/views/Orgs/OrgDonations.vue') },
      { path: 'donors', name: 'org-donors', component: () => import('@/views/Orgs/OrgDonors.vue') },
      { path: 'agents', name: 'org-agents', component: () => import('@/views/Orgs/OrgAgents.vue') },
      { path: 'admin-settings', name: 'org-crm-settings', component: () => import('@/views/Orgs/OrgCrmSettings.vue') },
      { path: 'installation', name: 'org-installation', component: () => import('@/views/Orgs/OrgInstallation.vue') }
    ],
    meta: {
      authRequired: true,
      title: 'Organization'
    }
  },
  {
    path: '*',
    name: 'otherwise',
    redirect: '/dashboard',
    meta: {
      authRequired: true,
      title: 'Dashboard'
    }
  }
];

const router = new VueRouter({
  mode: 'hash',
  base: import.meta.env.BASE_URL,
  routes
});

router.beforeEach(async (routeTo, routeFrom, next) => {
  if (routeTo.name) {
    NProgress.start();
  }

  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  function redirectToSignIn() {
    const nextPath = routeTo.fullPath.replace(/^\//, '');
    const query = nextPath && nextPath !== 'dashboard' ? { next: nextPath } : undefined;
    return next({ name: 'sign-in', query });
  }

  function redirectToSignUp() {
    const nextPath = routeTo.fullPath.replace(/^\//, '');
    const query = nextPath && nextPath !== 'dashboard' ? { next: nextPath } : undefined;
    return next({ name: 'sign-up', query });
  }

  if (!authRequired) {
    return next();
  }

  const currentUser = await store.dispatch('auth/validate');

  if (!currentUser) {
    return redirectToSignIn();
  }

  const userHasOrg = !_.isEmpty(currentUser.org);
  if (!userHasOrg && !routeTo.meta.allowWithoutOrg) {
    return redirectToSignUp();
  }

  // User is authenticated, and has an org
  return next();
});

router.afterEach(() => {
  NProgress.done();
});

export default router;
